import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Header, Button, Message, Icon } from 'semantic-ui-react';
import { LoadingWrapper } from '../common';
import { AssetProblemsTable } from './AssetProblemsTable';
import { getAssetProblemReportProblems, exportAssetProblemReport } from './assetProblemReportService';
import { formatDateToYyyyMmDd } from '../../utils/formatDate';

// Renders a team's problems inline on the dashboard (no route navigation), one panel per
// checked team filter.
export const TeamProblemsPanel = ({ teamId, teamLabel, startDate, endDate, onClose }) => {
    const [problems, setProblems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);

    const buildParams = useCallback(() => {
        const params = { team_id: teamId };
        if (startDate) params.start = formatDateToYyyyMmDd(startDate);
        if (endDate) params.end = formatDateToYyyyMmDd(endDate);
        return params;
    }, [teamId, startDate, endDate]);

    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setError('');
        getAssetProblemReportProblems(buildParams())
            .then((result) => { if (!cancelled) setProblems(result?.problems ?? result ?? []); })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [buildParams]);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await exportAssetProblemReport({ ...buildParams(), view: 'detail' });
        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    }, [buildParams]);

    return (
        <div className="apr-card apr-team-panel">
            <div className="apr-team-panel__header">
                <Header as="h4" className="apr-team-panel__title">
                    <Link to={`/teams/${teamId}/view`} target="_blank" rel="noopener noreferrer">{teamLabel}</Link>
                </Header>
                <div className="apr-team-panel__actions">
                    <Button basic size="small" icon labelPosition="left" onClick={handleExport} loading={exporting} disabled={exporting}>
                        <Icon name="file outline" />
                        Export
                    </Button>
                    <Button basic size="small" icon="close" onClick={onClose} aria-label={`Close ${teamLabel} panel`} />
                </div>
            </div>

            {error && (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <LoadingWrapper loading={!loaded}>
                {problems.length === 0 ? (
                    <p className="apr-empty-text">No asset problems for the selected filters.</p>
                ) : (
                    <AssetProblemsTable problems={problems} />
                )}
            </LoadingWrapper>
        </div>
    );
};
